import { applyPercentDiscount, roundToRuble } from '@snitch/domain'
import { cn } from '@snitch/ui/lib/cn'
import { DEVICE_FORMS, formatRub, MONTH_FORMS, plural } from '@snitch/ui/lib/format'
import { MotionValue } from '@snitch/ui/motion'
import { SeatDots } from '@snitch/ui/seat-dots'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Wallet } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { checkoutError } from '../../../components/checkout-errors.ts'
import { orpc } from '../../../lib/api.ts'
import { catalogQuery, formatDay, newIdempotencyKey, summaryQuery } from '../../../lib/billing.ts'

const unitPrice = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const Route = createFileRoute('/_authed/_shell/plans')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(catalogQuery),
      context.queryClient.ensureQueryData(summaryQuery),
    ]),
  component: Plans,
})

/** "Signal / Собрать тариф": devices × term × LTE, a live receipt and the pay bar. */
function Plans() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: catalog } = useSuspenseQuery(catalogQuery)
  const { data: summary } = useSuspenseQuery(summaryQuery)
  const sub = summary.subscription

  const [selection, setSelection] = useState(() => {
    const current = sub.devices ?? 4
    const devices = catalog.deviceTiers.some((t) => t.devices === current) ? current : 4
    return { devices, months: 3, lteGb: 0 }
  })
  // A new key per selection: a retry of the same choice replays, a different choice is a new order.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const [error, setError] = useState<string>()
  const { devices, months, lteGb } = selection
  const purchase = { kind: 'plan' as const, ...selection }
  const choose = (patch: Partial<typeof selection>) => {
    setSelection((s) => ({ ...s, ...patch }))
    setIdempotencyKey(newIdempotencyKey())
    setError(undefined)
  }

  const priced = useQuery({
    ...orpc.billing.quote.queryOptions({ input: purchase }),
    placeholderData: keepPreviousData,
  })
  const pay = useMutation(
    orpc.billing.checkout.mutationOptions({
      onSuccess: async (order) => {
        await queryClient.invalidateQueries({ queryKey: summaryQuery.queryKey })
        await queryClient.invalidateQueries({ queryKey: ['promos'] })
        await navigate({ to: '/orders/$orderId', params: { orderId: order.id } })
      },
      onError: (e) => {
        setError(checkoutError(e))
        void priced.refetch()
      },
    }),
  )

  const tier = catalog.deviceTiers.find((t) => t.devices === devices)
  const q = priced.data?.quote
  const plan = q?.lines.find((l) => l.kind === 'plan')
  const lte = q?.lines.find((l) => l.kind === 'lte')
  const context =
    sub.status === 'trial' && sub.paidUntil
      ? `Тестовый тариф до ${formatDay(sub.paidUntil)}`
      : sub.status === 'active' && sub.devices
        ? `Продление · сейчас ${sub.devices} ${plural(sub.devices, DEVICE_FORMS)}`
        : 'Новый тариф'

  return (
    <main className="flex min-h-dvh flex-col gap-7 px-5 pt-[max(20px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-7 md:px-8 md:pt-6 md:pb-10 xl:px-12 xl:pt-8 xl:pb-12">
      <header className="flex flex-col gap-7">
        <div className="flex h-11 items-center justify-between gap-3">
          <Link
            to="/"
            aria-label="Назад"
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-sunken"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
          <span className="min-w-0 text-center font-medium text-ink-2 text-sm">{context}</span>
          <span className="hidden size-11 shrink-0 sm:block" />
        </div>
        <h1 className="font-bold text-[34px] leading-[1.15] tracking-[-0.6px] xl:text-[38px]">
          Соберите<span className="block md:inline"> свой тариф</span>
        </h1>
      </header>

      <div className="grid min-w-0 gap-7 xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start xl:gap-8">
        <div className="flex min-w-0 flex-col gap-7">
          <Step no={1} title="Сколько устройств" hint="телефон, ноутбук, ТВ">
            <div className="flex flex-col gap-5 rounded-3xl border border-line bg-card p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="whitespace-nowrap font-semibold text-[min(30px,calc((100vw-136px)/8.3))] tracking-[-0.8px]">
                    {devices} {plural(devices, DEVICE_FORMS)}
                  </span>
                  {tier ? (
                    <span className="text-ink-2 text-sm">
                      ≈{formatRub(roundToRuble(tier.monthly / devices))} за каждое
                    </span>
                  ) : null}
                </div>
                <SeatDots count={devices} size={40} dot={9} className="text-blue" />
              </div>
              <Choices legend="Сколько устройств" className="gap-1.5">
                {catalog.deviceTiers.map((t) => (
                  <Choice
                    key={t.devices}
                    name="devices"
                    checked={t.devices === devices}
                    onSelect={() => choose({ devices: t.devices })}
                    className={cn(
                      'flex h-13 min-w-0 flex-1 items-center justify-center rounded-full font-semibold text-[22px]',
                      t.devices === devices ? 'bg-primary text-on-primary' : 'bg-sunken text-ink',
                    )}
                  >
                    {t.devices}
                    <span className="sr-only"> {plural(t.devices, DEVICE_FORMS)}</span>
                  </Choice>
                ))}
              </Choices>
            </div>
          </Step>

          <Step no={2} title="На какой срок">
            <Choices legend="На какой срок" className="flex-wrap gap-2 pt-2.5 xl:pt-0">
              {catalog.terms.map((term) => {
                const on = term.months === months
                const total = tier
                  ? applyPercentDiscount(tier.monthly * term.months, term.discountPercent)
                  : 0
                return (
                  <Choice
                    key={term.months}
                    name="months"
                    checked={on}
                    onSelect={() => choose({ months: term.months })}
                    className={cn(
                      'relative flex min-w-[95px] flex-1 items-center gap-3 rounded-[18px] border px-3 pt-[18px] pb-3 text-left xl:min-h-[122px] xl:py-[18px] xl:pl-4',
                      on ? 'border-primary bg-primary text-on-primary' : 'border-line bg-card',
                    )}
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-1.5 xl:gap-2">
                      <span className="font-semibold text-[13px]">
                        {term.months} {plural(term.months, MONTH_FORMS)}
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-semibold text-[22px] leading-tight tracking-[-0.4px]">
                          {formatRub(roundToRuble(total / term.months))}
                        </span>
                        <span className={cn('text-xs', on ? 'text-on-primary/75' : 'text-ink-2')}>
                          в месяц
                        </span>
                      </span>
                      <span className={cn('text-xs', on ? 'text-on-primary/75' : 'text-ink-2')}>
                        {unitPrice.format(total / 100 / term.months / devices)} ₽/устр.
                      </span>
                    </span>
                    {term.discountPercent > 0 ? (
                      <span className="absolute -top-2.5 left-1/2 flex shrink-0 -translate-x-1/2 flex-col items-center justify-center gap-0.5 rounded-[7px] bg-[#30d158] px-2 py-[3px] font-semibold text-[#08250f] text-[11px] leading-tight xl:static xl:h-[54px] xl:w-16 xl:translate-x-0 xl:rounded-xl xl:p-0 xl:text-[19px]">
                        <span>−{term.discountPercent}%</span>
                        <span className="hidden font-medium text-[11px] xl:block">скидка</span>
                      </span>
                    ) : null}
                    <span className="sr-only">{formatRub(total)} всего</span>
                  </Choice>
                )
              })}
            </Choices>
          </Step>

          <Step no={3} title="LTE Трафик">
            <Choices
              legend="LTE Трафик"
              className="flex-wrap gap-1.5 rounded-3xl border border-line bg-card p-[5px]"
            >
              {[0, ...catalog.lte.packsGb].map((gb) => (
                <Choice
                  key={gb}
                  name="lte"
                  checked={gb === lteGb}
                  onSelect={() => choose({ lteGb: gb })}
                  className={cn(
                    'flex h-14 min-w-[60px] flex-1 flex-col items-center justify-center gap-px rounded-[20px]',
                    gb === lteGb && 'bg-primary text-on-primary',
                  )}
                >
                  <span className="font-semibold text-[15px]">{gb === 0 ? 'Без' : `${gb} ГБ`}</span>
                  <span
                    className={cn('text-xs', gb === lteGb ? 'text-on-primary/75' : 'text-ink-2')}
                  >
                    {formatRub(gb * catalog.lte.pricePerGb)}
                  </span>
                </Choice>
              ))}
            </Choices>
            <p className="text-[13px] text-ink-2 leading-[1.4]">
              Неиспользованные гигабайты не сгорают — докупить можно в любой момент
            </p>
          </Step>
        </div>

        <div
          className="flex min-w-0 flex-col gap-5 xl:sticky xl:top-8"
          aria-busy={priced.isFetching}
        >
          {q && plan?.kind === 'plan' ? (
            <section className="rounded-3xl border border-line bg-card" aria-label="Чек">
              <div className="flex flex-col gap-3 px-5 pt-5 pb-[18px]">
                <h2 className="text-ink-2 text-xs">ЧЕК</h2>
                <ReceiptLine
                  label={`${plan.devices} ${plural(plan.devices, DEVICE_FORMS)} · ${plan.months} мес`}
                  old={plan.listPrice !== plan.price ? plan.listPrice : undefined}
                  value={formatRub(plan.price)}
                />
                {lte?.kind === 'lte' ? (
                  <ReceiptLine label={`LTE · ${lte.gb} ГБ`} value={formatRub(lte.price)} />
                ) : null}
                {q.discounts.map((d) => (
                  <ReceiptLine
                    key={d.source}
                    label={
                      d.source === 'promo'
                        ? `Промокод ${d.ref ?? ''} · −${d.percent}%`
                        : d.source === 'referral_welcome'
                          ? `Скидка друга · −${d.percent}%`
                          : 'Скидка'
                    }
                    value={`−${formatRub(d.amount)}`}
                    tone="green"
                  />
                ))}
                {q.bonusSpent > 0 ? (
                  <ReceiptLine
                    label="Бонусный баланс"
                    value={`−${formatRub(q.bonusSpent)}`}
                    tone="green"
                  />
                ) : null}
              </div>
              <div className="flex items-end justify-between border-line border-t px-5 pt-4 pb-[22px]">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-2">Экономия</span>
                  <span className="font-semibold text-[15px] text-green-ink">
                    <MotionValue>{formatRub(q.savings)}</MotionValue>
                  </span>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-[13px] text-ink-2">К оплате</span>
                  <span className="font-semibold text-[30px] tracking-[-0.5px]">
                    <MotionValue>{formatRub(q.total)}</MotionValue>
                  </span>
                </div>
              </div>
            </section>
          ) : null}

          {priced.isError ? (
            <div role="alert" className="rounded-2xl bg-red-soft p-4 text-sm">
              Не удалось рассчитать тариф.{' '}
              <button
                type="button"
                onClick={() => void priced.refetch()}
                className="font-semibold underline underline-offset-4"
              >
                Повторить
              </button>
            </div>
          ) : null}
          {q && (!priced.data?.payable || q.bonusSpent > 0) ? (
            <p className="flex items-start gap-2.5 rounded-2xl bg-sunken px-4 py-3.5 text-sm leading-[1.4]">
              <Wallet className="mt-px size-5 shrink-0" aria-hidden="true" />
              <span>
                {!priced.data?.payable ? (
                  <>
                    Онлайн-оплата скоро. Сейчас можно оплатить только бонусами: на балансе{' '}
                    {formatRub(summary.bonusBalance)}, не хватает{' '}
                    <MotionValue>{formatRub(q.total)}</MotionValue>.
                  </>
                ) : (
                  <>
                    Спишем {formatRub(q.bonusSpent)} бонусами. На балансе останется{' '}
                    {formatRub(summary.bonusBalance - q.bonusSpent)}.
                    {q.total === 0 ? ' Деньгами доплачивать не нужно.' : ''}
                  </>
                )}
              </span>
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="rounded-2xl bg-red-soft px-4 py-3 text-sm">
              {error}
            </p>
          ) : null}

          <div className="sticky bottom-[max(16px,env(safe-area-inset-bottom))] z-10 flex min-h-[76px] items-center gap-3 rounded-[32px] glass py-2 pr-2 pl-[22px] xl:static">
            <div className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="font-semibold text-xl tracking-[-0.5px]">
                <MotionValue>{q ? formatRub(q.total) : '…'}</MotionValue>
              </span>
              {priced.data?.period ? (
                <span className="text-ink-2 text-xs">
                  до {formatDay(priced.data.period.endsAt)}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              disabled={
                !q || !priced.data?.payable || pay.isPending || priced.isFetching || priced.isError
              }
              onClick={() => {
                if (!q || !priced.data) return
                setError(undefined)
                pay.mutate({
                  purchase,
                  expectedTotal: q.total,
                  quoteFingerprint: priced.data.quoteFingerprint,
                  idempotencyKey,
                })
              }}
              className="motion-button flex h-13 shrink-0 items-center gap-2 rounded-full bg-primary px-5 font-semibold text-[17px] text-on-primary transition-opacity disabled:opacity-45"
            >
              {pay.isPending ? 'Оплачиваем…' : 'Оплатить'}
              <ArrowRight className="size-[18px]" aria-hidden="true" />
            </button>
          </div>
          {q?.discounts.some((d) => d.source === 'promo') ? (
            <p className="text-[13px] text-ink-2 leading-[1.4]">
              Промокод действует на одну покупку тарифа. На мобильный интернет скидка не
              распространяется.
            </p>
          ) : null}
        </div>
      </div>
    </main>
  )
}

function Step({
  no,
  title,
  hint,
  children,
}: {
  no: number
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2.5">
        <span className="flex size-6 items-center justify-center rounded-full bg-sunken font-medium text-ink text-xs">
          {no}
        </span>
        <h2 className="shrink-0 whitespace-nowrap font-semibold text-[17px]">{title}</h2>
        {hint ? (
          <span className="min-w-0 flex-1 truncate text-right text-[11px] text-ink-2">{hint}</span>
        ) : null}
      </div>
      {children}
    </section>
  )
}

/** Native radios: arrow keys, screen readers and forms work out of the box. */
function Choices({
  legend,
  className,
  children,
}: {
  legend: string
  className?: string
  children: ReactNode
}) {
  return (
    <fieldset className={cn('flex min-w-0', className)}>
      <legend className="sr-only">{legend}</legend>
      {children}
    </fieldset>
  )
}

function Choice({
  name,
  checked,
  onSelect,
  className,
  children,
}: {
  name: string
  checked: boolean
  onSelect: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <label
      className={cn(
        'cursor-pointer has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ink has-[:focus-visible]:outline-offset-2',
        className,
      )}
    >
      <input type="radio" name={name} checked={checked} onChange={onSelect} className="sr-only" />
      {children}
    </label>
  )
}

function ReceiptLine({
  label,
  value,
  old,
  tone,
}: {
  label: string
  value: string
  old?: number
  tone?: 'green'
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="min-w-0 flex-1 break-words text-[15px] text-ink-2 [overflow-wrap:anywhere]">
        {label}
      </span>
      <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        {old ? <s className="text-[13px] text-ink-3">{formatRub(old)}</s> : null}
        <span className={cn('font-semibold text-[15px]', tone === 'green' && 'text-kb-green-ink')}>
          <MotionValue>{value}</MotionValue>
        </span>
      </span>
    </div>
  )
}
